export const DATE_PRESETS = Object.freeze({
  WEEK: 'week',
  MONTH: 'month',
  YTD: 'ytd'
});

export function handleDatePreset(type) {
  const today = new Date();
  const year = today.getFullYear().toString();
  let endDate = today;
  let startDate = null;

  switch(type) {
    case DATE_PRESETS.WEEK:
      // get the last full week orders from Monday - Sunday
      // find the previous sunday from today;
      // offset will be the remainder of today / 6
      // getDay() uses index 0-6 and day 0 means we don't need an offset
      const endDateOffset = (today.getDay() % 6);
      endDate = new Date(today);
      endDate.setDate(endDate.getDate() - endDateOffset);
      // startDate will be six days before endDate
      startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 6);
      break;
    case DATE_PRESETS.MONTH:
      // the month so far, from the 1st to current date
      startDate = new Date(year+"/"+(today.getMonth()+1));
      break;
    case DATE_PRESETS.YTD:
      // the year so far, from Jan 1st to current date/time
      startDate = new Date(year);
      break;
  }

  startDate = getSimpleDateString(startDate);
  endDate = getSimpleDateString(endDate);

  return { startDate, endDate };
}

export function getSimpleDateString(date) {
  return date?.toISOString().split('T')[0];
}

/**
 * Timezone-safe date parsing utilities
 * Addresses the common issue where Date parsing of YYYY-MM-DD strings
 * assumes UTC, causing off-by-one day issues in local timezones
 */

/**
 * Parse a date string treating it as a calendar date (ignores timezone)
 * Use this for publication dates, birth dates, etc. where timezone doesn't matter
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {Date|null} - Parsed Date object or null if invalid
 */
export function parseDateOnly(dateInput) {
  if (!dateInput) return null;
  
  try {
    // If it's already a Date object, return it
    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? null : dateInput;
    }
    
    // Handle string inputs - always extract just the date part
    if (typeof dateInput === 'string') {
      const dateMatch = dateInput.match(/^(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        const [year, month, day] = dateMatch[1].split('-').map(Number);
        return new Date(year, month - 1, day); // month is 0-indexed
      }
    }
    
    return null;
  } catch (e) {
    console.error('Error parsing date only:', e);
    return null;
  }
}

/**
 * Parse a datetime string respecting timezone (for availableFrom, etc.)
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {Date|null} - Parsed Date object or null if invalid
 */
export function parseDateTime(dateInput) {
  if (!dateInput) return null;
  
  try {
    // If it's already a Date object, return it
    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? null : dateInput;
    }
    
    // Handle string inputs
    if (typeof dateInput === 'string') {
      const date = new Date(dateInput);
      return isNaN(date.getTime()) ? null : date;
    }
    
    return null;
  } catch (e) {
    console.error('Error parsing datetime:', e);
    return null;
  }
}

/**
 * Parse a date string in a timezone-safe manner
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {Date|null} - Parsed Date object or null if invalid
 * @deprecated Use parseDateOnly or parseDateTime instead
 */
export function parseDate(dateInput) {
  if (!dateInput) return null;
  
  try {
    // If it's already a Date object, return it
    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? null : dateInput;
    }
    
    // Handle string inputs
    if (typeof dateInput === 'string') {
      // Extract just the date part for date-only fields (like publication date)
      // This prevents timezone shifts for dates that should be treated as local
      const dateMatch = dateInput.match(/^(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        const [year, month, day] = dateMatch[1].split('-').map(Number);
        return new Date(year, month - 1, day); // month is 0-indexed
      }
      
      // Handle other formats
      const date = new Date(dateInput);
      return isNaN(date.getTime()) ? null : date;
    }
    
    return null;
  } catch (e) {
    console.error('Error parsing date:', e);
    return null;
  }
}

/**
 * Format a Date object to YYYY-MM-DD string using local timezone
 * @param {Date} date - Date object
 * @returns {string|null} - Formatted date string or null
 */
export function formatDateForPicker(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return null;
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a Date object to HH:MM string using local timezone
 * @param {Date} date - Date object
 * @returns {string|null} - Formatted time string or null
 */
export function formatTimeForPicker(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return null;
  
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Create a Date object from date and time strings in local timezone
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @param {string} timeString - Time in HH:MM format (optional)
 * @returns {Date|null} - Combined Date object or null
 */
export function createDateTime(dateString, timeString = null) {
  if (!dateString) return null;
  
  try {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    
    if (timeString) {
      const [hours, minutes] = timeString.split(':').map(Number);
      date.setHours(hours, minutes, 0, 0);
    } else {
      date.setHours(0, 0, 0, 0);
    }
    
    return date;
  } catch (e) {
    console.error('Error creating date time:', e);
    return null;
  }
}

/**
 * Format a date for display using browser's locale
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} - Formatted date for display
 */
export function formatDateForDisplay(dateString) {
  if (!dateString) return '';
  
  try {
    const date = parseDate(dateString);
    return date ? date.toLocaleDateString() : dateString;
  } catch (e) {
    return dateString;
  }
}

/**
 * Compare two date values to check if they represent the same date
 * Handles different formats (e.g., "2025-06-17" vs "2025-06-17T00:00:00+00:00")
 * @param {string|Date} date1 - First date to compare
 * @param {string|Date} date2 - Second date to compare
 * @returns {boolean} - True if dates represent the same day
 */
export function isSameDate(date1, date2) {
  if (!date1 || !date2) return date1 === date2;
  
  // Convert both to YYYY-MM-DD format for comparison
  const d1 = parseDate(date1);
  const d2 = parseDate(date2);
  
  if (!d1 || !d2) return false;
  
  return formatDateForPicker(d1) === formatDateForPicker(d2);
}

/**
 * Format a Date object to local ISO string without timezone conversion
 * Returns YYYY-MM-DDTHH:MM:SS format using the date's local values
 * @param {Date} date - Date object
 * @returns {string|null} - Formatted datetime string or null
 */
export function formatLocalISOString(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return null;
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}